<template>
    <div class="space-y-4">
        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                :title="t('dashboard.student.currentClass')"
                :value="safeData.stats.currentClass || 'N/A'"
                :icon="Home"
                color="blue"
            />
            <StatCard
                :title="t('dashboard.student.totalMaterials')"
                :value="safeData.stats.totalMaterials || 0"
                :icon="FileText"
                color="purple"
            />
            <StatCard
                :title="t('dashboard.student.completedExams')"
                :value="safeData.stats.completedExams || 0"
                :icon="CheckCircle"
                color="green"
            />
            <StatCard
                :title="t('dashboard.student.progress')"
                :value="`${safeData.progressPercentage}%`"
                :icon="BarChart3"
                color="yellow"
            />
        </div>

        <!-- Recent Materials -->
        <MaterialsSection
            :title="t('dashboard.student.recentMaterials')"
            :materials="safeData.recentMaterials || []"
            :showViewAll="true"
            viewAllRoute="/student-materials"
            :showStatus="false"
            :showEdit="false"
            :showTeacher="true"
            :emptyStateTitle="t('dashboard.materials.emptyTitle')"
            :emptyStateMessage="t('dashboard.materials.emptyMessageStudent')"
            @open="handleOpenMaterial"
            @download="handleDownloadMaterial"
        />

        <!-- Progress Indicator -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div class="px-4 py-4 sm:p-5">
                <h3
                    class="text-base leading-6 font-medium text-gray-900 dark:text-white mb-3"
                >
                    {{ t("dashboard.student.learningProgress") }}
                </h3>
                <div class="space-y-3">
                    <div>
                        <div class="flex items-center justify-between mb-2">
                            <span
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ t("dashboard.student.overallProgress") }}
                            </span>
                            <span
                                class="text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ safeData.progressPercentage }}%
                            </span>
                        </div>
                        <div
                            class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3"
                        >
                            <div
                                class="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-300"
                                :style="{
                                    width: `${safeData.progressPercentage}%`,
                                }"
                            ></div>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                        <div class="text-center">
                            <div
                                class="text-2xl font-bold text-blue-600 dark:text-blue-400"
                            >
                                {{ safeData.stats.totalExams || 0 }}
                            </div>
                            <div
                                class="text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ t("dashboard.student.totalExams") }}
                            </div>
                        </div>
                        <div class="text-center">
                            <div
                                class="text-2xl font-bold text-green-600 dark:text-green-400"
                            >
                                {{ safeData.stats.completedExams || 0 }}
                            </div>
                            <div
                                class="text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ t("dashboard.student.completed") }}
                            </div>
                        </div>
                        <div class="text-center">
                            <div
                                class="text-2xl font-bold text-yellow-600 dark:text-yellow-400"
                            >
                                {{
                                    (safeData.stats.totalExams || 0) -
                                    (safeData.stats.completedExams || 0)
                                }}
                            </div>
                            <div
                                class="text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ t("dashboard.student.remaining") }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { StudentDashboardData, MaterialItem } from "@/types/dashboard";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import StatCard from "./StatCard.vue";
import MaterialsSection from "./MaterialsSection.vue";
import { Home, CheckCircle, BarChart3, FileText } from "@lucide/vue";

const { t } = useI18n();

interface Props {
    data: StudentDashboardData | null;
}

const props = defineProps<Props>();

// Computed property to safely access data
const safeData = computed(
    () =>
        props.data || {
            stats: {
                currentClass: "N/A",
                totalExams: 0,
                completedExams: 0,
                totalMaterials: 0,
            },
            recentMaterials: [],
            progressPercentage: 0,
        },
);

const handleDownloadMaterial = async (material: MaterialItem) => {
    try {
        // Import materialApi dynamically to avoid circular dependencies
        const { materialApi } = await import("@/api/material");

        // Download the material
        const blob = await materialApi.download(material.id);

        // Create a temporary link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = material.title || "material";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Failed to download material:", error);
        // You could add a toast notification here
    }
};

const handleOpenMaterial = (material: MaterialItem) => {
    if (material.type === "url" && material.url) {
        window.open(material.url, "_blank");
    } else if (material.type === "file" && material.fileUrl) {
        window.open(material.fileUrl, "_blank");
    }
};
</script>
